/**
 * PropertyGate — anonymous view counter + email capture modal.
 *
 * Tracks how many properties an anonymous visitor has viewed in localStorage.
 * After VIEW_LIMIT, shows a modal asking for their email to keep browsing.
 * Submitting the email logs a lead and unlocks unlimited browsing forever
 * (on that device).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Home } from 'lucide-react';
import { toast } from 'sonner';
import { SmsOptInCheckbox } from './SmsConsentDisclosure';

const API = process.env.REACT_APP_BACKEND_URL;
const VIEW_LIMIT = 3;
const KEY_COUNT = 'br_view_count';
const KEY_UNLOCKED = 'br_unlocked_email';

export const trackPropertyView = () => {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(KEY_UNLOCKED)) return; // already unlocked
  const current = parseInt(localStorage.getItem(KEY_COUNT) || '0', 10);
  localStorage.setItem(KEY_COUNT, String(current + 1));
};

export const isGated = () => {
  if (typeof window === 'undefined') return false;
  if (localStorage.getItem(KEY_UNLOCKED)) return false;
  const current = parseInt(localStorage.getItem(KEY_COUNT) || '0', 10);
  return current >= VIEW_LIMIT;
};

export const unlockLocal = (email) => {
  localStorage.setItem(KEY_UNLOCKED, email);
};

const PropertyGate = ({ context = {} }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const check = useCallback(() => {
    if (isGated()) setOpen(true);
  }, []);

  useEffect(() => {
    check();
    // Re-check after any track call — poll lightly
    const t = setInterval(check, 1500);
    return () => clearInterval(t);
  }, [check]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Email required');
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/leads/gating`, { email, name, phone, sms_opt_in: smsOptIn, context });
      unlockLocal(email);
      setOpen(false);
      toast.success("You're all set — enjoy browsing.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="property-gate-modal"
      >
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-[#BCA480]/20 flex items-center justify-center mb-2">
            <Lock className="w-6 h-6 text-[#BCA480]" />
          </div>
          <DialogTitle className="text-center">Keep exploring</DialogTitle>
          <DialogDescription className="text-center">
            You've viewed {VIEW_LIMIT} properties. Enter your email to unlock unlimited browsing — plus first access to new listings.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-2">
          <div>
            <Label>Email <span className="text-red-500">*</span></Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
              data-testid="gate-email"
            />
          </div>
          <div>
            <Label>Name (optional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="gate-name" />
          </div>
          <div>
            <Label>Phone (optional)</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(901) 555-0000" data-testid="gate-phone" />
            <SmsOptInCheckbox checked={smsOptIn} onChange={setSmsOptIn} />
          </div>
          <Button
            type="submit"
            className="w-full bg-[#BCA480] hover:bg-[#a89470] text-white"
            disabled={submitting}
            data-testid="gate-submit"
          >
            <Home className="w-4 h-4 mr-2" />
            {submitting ? 'Unlocking…' : 'Unlock & Continue Browsing'}
          </Button>
          <p className="text-[11px] text-center text-gray-500">
            We'll never sell your email. Unsubscribe anytime.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyGate;
